/**
 * "Fix once, fixes every resume" — the master-resume warning (Recruiter Check
 * BR-1).
 *
 * Users do not infer that the master resume is stamped onto every generated
 * PDF, so a typo there quietly rides along on every application. This module
 * owns BOTH the count and the sentence, so the resume selector, the Edit Resume
 * banner and any future surface always say the same thing.
 *
 * Two count sources, deliberately:
 *   • the SELECTOR reads `Resume.lint_high_count`, a tiny number the save path
 *     persists — the list must never run N lint passes to draw a row;
 *   • the EDITOR counts the live lint pass it already has, so the banner tracks
 *     what the user is doing right now (including "Mark resolved").
 * Both mean the same thing: UNRESOLVED, HIGH-severity, LINT findings.
 */
import { buildScorecard } from './scorecard';
import type { LintFinding } from './scorecard';
import type { ResumeJSON, Resume } from '../types';

/**
 * Unresolved high-severity LINT findings in the live editor state.
 *
 * Browser-computed findings (AI-tells, quantification…) are excluded — only
 * findings carrying a server `rule` count, which is what the persisted
 * `lint_high_count` counts too. Returns 0 while the pass is still loading, so
 * the banner never flashes a number it is about to correct.
 */
export function liveLintHighCount(
  resume: ResumeJSON,
  lintFindings: LintFinding[] | null | undefined,
): number {
  if (!lintFindings || !lintFindings.length) return 0;
  const resolved = new Set(resume.scorecard_resolved || []);
  return buildScorecard(resume, { lintFindings, resolvedIds: resolved })
    .dimensions
    .flatMap((d) => d.findings)
    .filter((f) => !!f.rule && f.severity === 'high' && !resolved.has(f.id!))
    .length;
}

/** The persisted count on a resume record; absent/garbage ⇒ 0 (no banner). */
export function recordLintHighCount(resume?: Pick<Resume, 'lint_high_count'> | null): number {
  const n = Number(resume?.lint_high_count);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

/**
 * The banner sentence. Plain words, and it TEACHES the mental model (master →
 * every bespoke copy) rather than assuming the user already has it.
 */
export function masterLintBannerText(count: number): string {
  const issues = count === 1 ? '1 issue' : `${count} issues`;
  const them = count === 1 ? 'it' : 'them';
  return `${issues} in this master resume will be copied onto every resume you generate — fix ${them} once here.`;
}

/** Compact form for a selector row, where there is no space for the sentence. */
export function masterLintChipText(count: number): string {
  return count === 1 ? '1 issue copied to every resume' : `${count} issues copied to every resume`;
}
